'use client';

import { useEffect, useState } from 'react';
import { List } from 'lucide-react';

interface TocItem {
  id: string;
  text: string;
}

export default function TableOfContents() {
  const [tocItems, setTocItems] = useState<TocItem[]>([]);
  const [activeId, setActiveId] = useState('');
  const [isOpen, setIsOpen] = useState(true);

  useEffect(() => {
    let observer: IntersectionObserver | null = null;
    let headings: NodeListOf<HTMLHeadingElement> | null = null;

    const article = document.querySelector('article');
    if (!article) return;

    headings = article.querySelectorAll('h2');
    const items: TocItem[] = [];

    headings.forEach((heading, index) => {
      const text = heading.textContent || '';
      if (!text || text.length < 3) return;

      let id = heading.id;
      if (!id) {
        id = `heading-${index}-${text.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
        heading.id = id;
      }
      items.push({ id, text });
    });

    setTocItems(items);

    observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) {
          visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
          setActiveId(visible[0].target.id);
        }
      },
      {
        rootMargin: '-100px 0px -66% 0px',
        threshold: Array.from({ length: 21 }, (_, i) => i * 0.05),
      }
    );

    headings.forEach((h) => {
      if (h.id) observer!.observe(h);
    });

    return () => {
      if (observer && headings) {
        headings.forEach((h) => {
          if (h.id) observer!.unobserve(h);
        });
      }
    };
  }, []);

  // Auto-scroll TOC to keep active item visible
  useEffect(() => {
    if (!activeId) return;
    const timer = setTimeout(() => {
      const btn = document.querySelector(`aside button[data-heading-id="${activeId}"]`) as HTMLElement;
      if (!btn) return;
      const nav = btn.closest('nav') as HTMLElement;
      if (!nav) return;

      const btnTop = btn.offsetTop - nav.scrollTop;
      const btnBottom = btnTop + btn.clientHeight;
      if (btnTop < 0 || btnBottom > nav.clientHeight) {
        nav.scrollTo({
          top: btn.offsetTop - nav.clientHeight / 2 + btn.clientHeight / 2,
          behavior: 'smooth',
        });
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [activeId]);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.pageYOffset - 100;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  if (tocItems.length === 0) return null;

  return (
    <>
      {/* Mobile Toggle */}
      <div className="lg:hidden sticky top-20 z-40 mb-8">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full bg-white border-2 border-gray-200 rounded-lg px-4 py-3 flex items-center justify-between hover:border-blue-500 transition-colors"
        >
          <span className="flex items-center gap-2 font-semibold text-gray-700">
            <List className="w-5 h-5" />
            Table of Contents
          </span>
          <span className="text-blue-600">{isOpen ? '−' : '+'}</span>
        </button>

        {isOpen && (
          <div className="mt-2 bg-white border-2 border-gray-200 rounded-lg p-4 shadow-lg">
            <nav>
              <ul className="space-y-2">
                {tocItems.map((item) => (
                  <li key={item.id}>
                    <button
                      data-heading-id={item.id}
                      onClick={() => { scrollTo(item.id); setIsOpen(false); }}
                      className={`text-left w-full py-1 px-2 rounded transition-colors ${
                        activeId === item.id
                          ? 'text-blue-600 font-semibold bg-blue-50'
                          : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
                      }`}
                    >
                      {item.text}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        )}
      </div>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:block h-full">
        <div className="sticky top-24 flex flex-col max-h-[calc(100vh-12rem)]">
          <div className="bg-white border-2 border-gray-200 rounded-lg p-6 shadow-md flex-1 min-h-0 flex flex-col">
            <h3 className="text-lg font-bold text-gray-700 mb-4 flex items-center gap-2 flex-shrink-0">
              <List className="w-5 h-5 text-blue-600" />
              Table of Contents
            </h3>
            <nav className="overflow-y-auto flex-1 min-h-0">
              <ul className="space-y-2">
                {tocItems.map((item) => (
                  <li key={item.id}>
                    <button
                      data-heading-id={item.id}
                      onClick={() => scrollTo(item.id)}
                      className={`text-left w-full py-1.5 px-3 rounded transition-all duration-200 ${
                        activeId === item.id
                          ? 'text-blue-600 font-semibold bg-blue-50 border-l-4 border-blue-600'
                          : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50 border-l-4 border-transparent'
                      }`}
                    >
                      {item.text}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="mt-4 w-full bg-gradient-to-r from-blue-600 to-blue-500 text-white py-3 px-4 rounded-lg font-semibold hover:shadow-lg transition-all flex-shrink-0"
            >
              ↑ Back to Top
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
